import { Fragment, type FunctionComponent, useEffect, useState } from 'react'

import { type Application } from '../../types/application'

import { showAlert } from 'src/actions/alertsActions'
import { startLoading, stopLoading } from 'src/actions/loadingActions'

import useApplicationsQuery from 'src/hooks/services/graph/queries/useApplicationsQuery'

import Banner from 'src/components/Common/Banner'
import Count from 'src/components/Common/Count'
import Section from 'src/components/Common/Section'
import Spacer from 'src/components/Common/Spacer'
import ApplicationDrawer from 'src/components/Drawers/ApplicationDrawer'

import { Iconly } from 'react-iconly'
import { FcSettings } from 'react-icons/fc'
import { IoCloseCircleOutline } from 'react-icons/io5'
import { type RootStateOrAny, useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import ApplicationTable from '../../components/Tables/ApplicationTable'

const Applications: FunctionComponent = (): JSX.Element => {
  const [applications, setApplications] = useState<Application[]>([])
  const [filtered, setFiltered] = useState<Application[]>([])
  const [showSearch, setShowSearch] = useState<boolean>(false)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const account = useSelector((state: RootStateOrAny) => state.account)

  const { data: applicationsData, isLoading: isLoadingApplications, error } = useApplicationsQuery(account.id)

  /* filter the applications by search term */
  const filter = (value: string) => {
    const searchTerm = value.toLowerCase()

    if (!Array.isArray(applications)) {
      setFiltered([])
      return
    }

    setFiltered(
      applications.filter(
        (application: Application) =>
          application.displayName.toLowerCase().indexOf(searchTerm) !== -1 || application.appId.toLowerCase().indexOf(searchTerm) !== -1
      )
    )
  }

  /* get the applications */
  useEffect(() => {
    dispatch(startLoading())

    if (applicationsData) {
      // Ensure applicationsData is an array
      const appsArray = Array.isArray(applicationsData) ? applicationsData : []
      setApplications(appsArray)
      setFiltered(appsArray)
    }

    if (error) {
      dispatch(
        showAlert({
          type: 'error',
          message: 'We were unable to retrieve your applications.',
          component: 'Applications',
          error: error as Error
        })
      )
      dispatch(stopLoading())
    }
  }, [applicationsData, error, dispatch])

  /* stop loading when the applications have been processed */
  useEffect(() => {
    if (filtered.length > 0 || (applications.length === 0 && !isLoadingApplications)) {
      dispatch(stopLoading())
    }
  }, [filtered, applications.length, isLoadingApplications, dispatch])

  const handleEditApplication = (application: Application) => {
    navigate(`/applications/${application.appId}`)
  }

  return (
    <Fragment>
      <Section>
        <div className="my-5 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-1 mdlg:grid-cols-2 xl:grid-cols-4">
          <Count
            icon={<FcSettings className="h-full w-full text-th-warning" />}
            value={Array.isArray(applications) ? applications.length : 0}
            label="All Applications"
            className="cursor-pointer"
            onClick={() => setFiltered(Array.isArray(applications) ? applications : [])}
            active={true}
          />
        </div>
      </Section>
      <Section>
        <Banner>
          <Spacer className="hidden sm:block" />
          {/* search */}
          {showSearch ? (
            <Fragment>
              <input type="text" className="rounded-lg p-2 font-body text-sm" placeholder="Search" autoFocus onChange={(e) => filter(e.target.value)} />
              <IoCloseCircleOutline className="h-6 w-6 cursor-pointer" onClick={() => setShowSearch(false)} />
            </Fragment>
          ) : (
            <Iconly name="Search" set="light" className="cursor-pointer" onClick={() => setShowSearch(true)} />
          )}
        </Banner>
        <Spacer />
        <ApplicationTable applications={filtered} onEditApplication={handleEditApplication} />
      </Section>

      <ApplicationDrawer />
    </Fragment>
  )
}

export default Applications
